import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Card from '../ui/Card';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/ConfirmDialog';
import GridLayoutPanel from './GridLayoutPanel';
import PageMarginsPanel from './PageMarginsPanel';
import CellSettingsPanel from './CellSettingsPanel';
import TemplatePanel from './TemplatePanel';
import AddBlockMenu from './AddBlockMenu';
import PositionPanel from './PositionPanel';
import SizePanel from './SizePanel';
import MultiSelectPanel from './MultiSelectPanel';
import BLOCK_TYPES from './blocks/registry';
import { clampToCellBounds } from './geometry';
import usePrintableTemplates, {
  fetchPrintableTemplate,
  createPrintableTemplate,
  updatePrintableTemplate,
  deletePrintableTemplate,
} from '../../hooks/usePrintableTemplates';
import { parseApiError } from '../../utils/apiError';

// Physical page size the design targets (US Letter). Everything below is
// positioned as a % of this, not a fixed px-per-inch, so the on-screen page
// (responsive, see .ss-print__page) and the eventual @media print page
// (Stage 5, sized to true physical inches) both derive from the same math.
const PAGE_WIDTH_IN = 8.5;
const PAGE_HEIGHT_IN = 11;

const DEFAULT_ROWS = 3;
const DEFAULT_COLUMNS = 2;
const DEFAULT_PAGE_MARGIN_IN = 0.12;
const DEFAULT_CELL_PADDING_IN = 0.1;

// Shared by initial state and the New/Discard resets below, so "blank
// template" is defined once.
const defaultPageMargins = () => ({
  locked: true,
  top: DEFAULT_PAGE_MARGIN_IN,
  right: DEFAULT_PAGE_MARGIN_IN,
  bottom: DEFAULT_PAGE_MARGIN_IN,
  left: DEFAULT_PAGE_MARGIN_IN,
});
const defaultCellPadding = () => ({
  locked: true,
  top: DEFAULT_CELL_PADDING_IN,
  right: DEFAULT_CELL_PADDING_IN,
  bottom: DEFAULT_CELL_PADDING_IN,
  left: DEFAULT_CELL_PADDING_IN,
});

// A comparable snapshot of everything a save persists (see handleSave's
// `body`), used to tell whether the designer has unsaved changes: the
// Template panel's Save/Discard buttons are enabled only when the current
// state's snapshot differs from the one captured at the last load/save.
const buildSnapshot = ({ name, rows, columns, pageMargins, cellPadding, showBoundaries, blocksByType }) =>
  JSON.stringify({
    name: name.trim(),
    grid: { rows, columns },
    page: { marginsIn: pageMargins, cellPaddingIn: cellPadding, showBoundaries },
    // A text block's width/height are auto-measured from its rendered DOM
    // (see TextBlockLayer), not something the user sets directly, and can
    // drift by a sub-pixel amount between the values a template was saved
    // with and what the browser re-measures them as on load — excluded
    // here so that harmless drift doesn't read as an unsaved change right
    // after opening a clean template. Any real content change (text,
    // font, size, weight, style, align) still shows up via those fields.
    // image/QR's own width/height/size stay included — the user directly
    // drag-resizes those, so a change there is a real change. `collapsed`
    // (text/QR) is UI state, not content — excluded so expanding/
    // collapsing a properties panel doesn't read as an unsaved change.
    elements: BLOCK_TYPES.flatMap(({ type, serialize }) =>
      blocksByType[type].map((block) => {
        const serialized = serialize(block);
        if (type === 'text') {
          const { width, height, collapsed, ...rest } = serialized;
          return rest;
        }
        if (type === 'qr') {
          const { collapsed, ...rest } = serialized;
          return rest;
        }
        return serialized;
      })
    ),
  });

const blankBlocksByType = { text: [], image: [], qr: [] };

// Remembers whichever template was last loaded or saved, so leaving the
// print page and coming back — including a plain refresh — restores it
// instead of dropping back to a blank new template. Only ever set from a
// successful load/save (see applyTemplate), so it's never a dirty/unsaved
// template being restored.
const LAST_TEMPLATE_STORAGE_KEY = 'ss-print-last-template-id';

const readLastTemplateId = () => {
  try {
    return window.localStorage.getItem(LAST_TEMPLATE_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeLastTemplateId = (id) => {
  try {
    if (id) window.localStorage.setItem(LAST_TEMPLATE_STORAGE_KEY, id);
    else window.localStorage.removeItem(LAST_TEMPLATE_STORAGE_KEY);
  } catch {
    // Storage unavailable (e.g. private browsing) — restoring on the next
    // visit just won't happen, current session is unaffected.
  }
};

export default function PrintableMasterGrid() {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { items: templates, loading: templatesLoading, reload: reloadTemplates } = usePrintableTemplates();

  const [templateId, setTemplateId] = useState(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleNameChange = (value) => {
    setName(value);
    setNameError(null);
  };

  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  // locked: single value applied to all four sides; unlocked: independent
  // per-side values. All four are kept populated in both modes so toggling
  // never loses a previously-set independent value. Same shape/pattern for
  // pageMargins and cellPadding.
  const [pageMargins, setPageMargins] = useState(defaultPageMargins);
  const [cellPadding, setCellPadding] = useState(defaultCellPadding);
  const [showBoundaries, setShowBoundaries] = useState(true);

  // One state array per registered block type (see blocks/registry.js).
  // `elements` is only ever assembled as a flat array at save time —
  // see handleSave — the grid itself never branches on `type` name.
  const [textBlocks, setTextBlocks] = useState([]);
  const [imageBlocks, setImageBlocks] = useState([]);
  const [qrBlocks, setQrBlocks] = useState([]);
  // [{ type, id }] of every currently-selected block. Click replaces the
  // selection with just that block; Ctrl/Cmd+Click toggles a block's
  // membership; Shift+Click extends the selection (add-only — a free-form
  // canvas has no natural order for a "range", so Shift always adds rather
  // than toggling, per the standard OS selection model this mirrors).
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  // The PRIMARY cell's own content box — every block layer renders inside
  // it at the same size, so its rect is what group-drag math below uses to
  // convert mouse-movement pixels to inches, the same way each layer's own
  // single-block drag does with its own (identically-sized) container ref.
  const cellContentRef = useRef(null);

  const blocksByType = { text: textBlocks, image: imageBlocks, qr: qrBlocks };
  const settersByType = { text: setTextBlocks, image: setImageBlocks, qr: setQrBlocks };

  // The snapshot captured at the last load/save — compared against the
  // live state below (`isDirty`) to drive the Template panel's Save/
  // Discard buttons. Starts as the blank/default state's own snapshot, so
  // a brand-new unsaved template isn't considered "dirty" until the user
  // actually changes something from those defaults.
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    buildSnapshot({
      name: '',
      rows: DEFAULT_ROWS,
      columns: DEFAULT_COLUMNS,
      pageMargins: defaultPageMargins(),
      cellPadding: defaultCellPadding(),
      showBoundaries: true,
      blocksByType: blankBlocksByType,
    })
  );

  const applyTemplate = (doc) => {
    setTemplateId(doc._id);
    writeLastTemplateId(doc._id);
    const nextName = doc.name || '';
    const nextRows = doc.grid?.rows ?? DEFAULT_ROWS;
    const nextColumns = doc.grid?.columns ?? DEFAULT_COLUMNS;
    const nextPageMargins = doc.page?.marginsIn ? { ...defaultPageMargins(), ...doc.page.marginsIn } : defaultPageMargins();
    const nextCellPadding = doc.page?.cellPaddingIn ? { ...defaultCellPadding(), ...doc.page.cellPaddingIn } : defaultCellPadding();
    const nextShowBoundaries = doc.page?.showBoundaries ?? true;
    const elements = doc.elements || [];
    const nextBlocksByType = {};
    BLOCK_TYPES.forEach(({ type, create }) => {
      nextBlocksByType[type] = elements.filter((el) => el.type === type).map((el) => create(el));
    });

    setName(nextName);
    setNameError(null);
    setRows(nextRows);
    setColumns(nextColumns);
    setPageMargins(nextPageMargins);
    setCellPadding(nextCellPadding);
    setShowBoundaries(nextShowBoundaries);
    BLOCK_TYPES.forEach(({ type }) => settersByType[type](nextBlocksByType[type]));
    setSelectedBlocks([]);
    setSavedSnapshot(
      buildSnapshot({
        name: nextName,
        rows: nextRows,
        columns: nextColumns,
        pageMargins: nextPageMargins,
        cellPadding: nextCellPadding,
        showBoundaries: nextShowBoundaries,
        blocksByType: nextBlocksByType,
      })
    );
  };

  const resetToNew = () => {
    setTemplateId(null);
    writeLastTemplateId(null);
    setName('');
    setNameError(null);
    setRows(DEFAULT_ROWS);
    setColumns(DEFAULT_COLUMNS);
    const blankPageMargins = defaultPageMargins();
    const blankCellPadding = defaultCellPadding();
    setPageMargins(blankPageMargins);
    setCellPadding(blankCellPadding);
    setShowBoundaries(true);
    BLOCK_TYPES.forEach(({ type }) => settersByType[type]([]));
    setSelectedBlocks([]);
    setSavedSnapshot(
      buildSnapshot({
        name: '',
        rows: DEFAULT_ROWS,
        columns: DEFAULT_COLUMNS,
        pageMargins: blankPageMargins,
        cellPadding: blankCellPadding,
        showBoundaries: true,
        blocksByType: blankBlocksByType,
      })
    );
  };

  const addBlock = (type, overrides) => {
    const blockType = BLOCK_TYPES.find((bt) => bt.type === type);
    if (!blockType) return;
    const newBlock = blockType.create(overrides);
    settersByType[type]((prev) => [...prev, newBlock]);
    setSelectedBlocks([{ type, id: newBlock.id }]);
  };

  // Click replaces the whole selection with just this block. Ctrl/Cmd+Click
  // toggles this block's membership (can add or remove). Shift+Click
  // extends the selection (add-only — a free-form canvas has no natural
  // order for a "range", so Shift always adds rather than toggling).
  // Passed down to every block layer as `onSelect`, called from each
  // layer's own mousedown/double-click handlers.
  const handleSelectBlock = (type, id, e) => {
    if (e?.metaKey || e?.ctrlKey) {
      setSelectedBlocks((prev) => {
        const exists = prev.some((sb) => sb.type === type && sb.id === id);
        return exists ? prev.filter((sb) => !(sb.type === type && sb.id === id)) : [...prev, { type, id }];
      });
    } else if (e?.shiftKey) {
      setSelectedBlocks((prev) => (prev.some((sb) => sb.type === type && sb.id === id) ? prev : [...prev, { type, id }]));
    } else {
      setSelectedBlocks([{ type, id }]);
    }
  };

  // A plain (no-modifier) mousedown on a block that's already part of a
  // multi-selection is ambiguous until the mouse moves: it could be a
  // plain click (collapse the selection to just this block, standard OS
  // behavior) or a click+drag (move the whole selection together, leaving
  // it intact). The decision is deferred to the effect below, keyed by
  // this ref + `groupDragTick`.
  const groupDragCandidate = useRef(null);
  const [groupDragTick, setGroupDragTick] = useState(0);

  const startGroupDragCandidate = (type, id, e) => {
    groupDragCandidate.current = {
      type,
      id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      moved: false,
      // Selection doesn't change once a group drag starts — the pressed
      // block is already a member, by the caller's own gating condition —
      // so it's safe to snapshot every selected block's starting position
      // right away instead of deferring it to the first move.
      startBlocks: selectedBlocks.map((sb) => {
        const block = blocksByType[sb.type].find((b) => b.id === sb.id);
        return {
          type: sb.type,
          id: sb.id,
          x: block.x,
          y: block.y,
          width: block.width ?? block.size,
          height: block.height ?? block.size,
        };
      }),
    };
    setGroupDragTick((t) => t + 1);
  };

  // Mousedown on the PRIMARY cell's empty background (not on a block) is
  // the same kind of ambiguous gesture: a plain click clears the
  // selection, a click+drag rubber-bands a rectangle and selects every
  // block it overlaps. Deferred to its own effect below, keyed by this ref
  // + `marqueeTick`.
  const marqueeCandidate = useRef(null);
  const [marqueeTick, setMarqueeTick] = useState(0);
  // Live rectangle (in inches, relative to the usable area) rendered as
  // the marquee overlay while a rectangle-select drag is in progress.
  const [marqueeRect, setMarqueeRect] = useState(null);

  const startMarquee = (e) => {
    if (e.target !== e.currentTarget) return;
    marqueeCandidate.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      moved: false,
    };
    setMarqueeTick((t) => t + 1);
  };

  // Position edits go through here too (from PositionPanel, not just
  // on-canvas drag), clamped the same way, so typing an out-of-range X/Y
  // can't push a block outside the PRIMARY cell's border either. Only
  // meaningful for a single selected block — PositionPanel/SizePanel/the
  // properties panel all render null when more than one block is selected.
  const patchSelectedBlock = (patch) => {
    if (selectedBlocks.length !== 1) return;
    const { type, id } = selectedBlocks[0];
    settersByType[type]((prev) =>
      prev.map((b) => {
        if (b.id !== id) return b;
        const merged = { ...b, ...patch };
        if ('x' in patch || 'y' in patch) {
          // Falls back to `size` for block types that are always square
          // (e.g. QR) and so don't carry independent width/height.
          const width = merged.width ?? merged.size;
          const height = merged.height ?? merged.size;
          const { x, y } = clampToCellBounds(merged.x, merged.y, width, height, usableWidthIn, usableHeightIn);
          return { ...merged, x, y };
        }
        return merged;
      })
    );
  };

  // Removes every currently-selected block, regardless of type — resolved
  // generically through settersByType. Shared by the Delete/Backspace
  // shortcut below and the trash-icon button in each type's properties
  // panel (e.g. TextPropertiesPanel), which only ever has a single block
  // selected when it's visible.
  const deleteSelectedBlocks = () => {
    if (selectedBlocks.length === 0) return;
    BLOCK_TYPES.forEach(({ type }) => {
      const idsToDelete = selectedBlocks.filter((sb) => sb.type === type).map((sb) => sb.id);
      if (idsToDelete.length === 0) return;
      settersByType[type]((prev) => prev.filter((b) => !idsToDelete.includes(b.id)));
    });
    setSelectedBlocks([]);
  };

  // Wipes every block of every type from the PRIMARY cell (the design's
  // grid/margins/padding/name are untouched) — distinct from deleting the
  // current *selection*: this clears regardless of what's selected.
  const handleClearAllBlocks = async () => {
    const hasAnyBlocks = BLOCK_TYPES.some(({ type }) => blocksByType[type].length > 0);
    if (!hasAnyBlocks) return;
    const ok = await confirm({
      title: 'Clear All',
      message: 'Remove every element from this template? This can\'t be undone.',
      confirmLabel: 'Clear all',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    BLOCK_TYPES.forEach(({ type }) => settersByType[type]([]));
    setSelectedBlocks([]);
  };

  useEffect(() => {
    const handler = (e) => {
      const active = document.activeElement;
      const isInputField =
        active &&
        (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT' || active.isContentEditable);
      if (isInputField) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelectedBlocks(BLOCK_TYPES.flatMap(({ type }) => blocksByType[type].map((b) => ({ type, id: b.id }))));
        return;
      }

      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (selectedBlocks.length === 0) return;
      e.preventDefault();
      deleteSelectedBlocks();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlocks, textBlocks, imageBlocks, qrBlocks]);

  const handleSelectTemplate = async (id) => {
    if (!id) {
      resetToNew();
      return;
    }
    try {
      const doc = await fetchPrintableTemplate(id);
      applyTemplate(doc);
    } catch (err) {
      // Covers a stale restored id (e.g. the template was since deleted) —
      // clear it so the next visit doesn't keep retrying a dead id.
      writeLastTemplateId(null);
      addToast('Could not load template', { variant: 'error' });
    }
  };

  // Restore whatever template was last loaded or saved, so leaving the
  // print page and coming back — including a plain refresh — picks up
  // where the user left off instead of a blank new template.
  useEffect(() => {
    const lastId = readLastTemplateId();
    if (lastId) handleSelectTemplate(lastId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDiscard = () => {
    if (templateId) handleSelectTemplate(templateId);
    else resetToNew();
  };

  // The whole design — grid, page margins, cell padding, elements — travels
  // as one document per save. `elements` is assembled fresh here from every
  // registered block type's own state + serializer; nothing else in this
  // component ever builds a combined elements array. Shared by Save (which
  // keeps the current name) and Save As (which overrides it).
  const buildTemplateBody = (nameOverride) => ({
    name: nameOverride.trim(),
    grid: { rows, columns },
    page: {
      widthIn: PAGE_WIDTH_IN,
      heightIn: PAGE_HEIGHT_IN,
      marginsIn: pageMargins,
      cellPaddingIn: cellPadding,
      showBoundaries,
    },
    elements: BLOCK_TYPES.flatMap(({ type, serialize }) => blocksByType[type].map(serialize)),
  });

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError('Name is required');
      return;
    }
    setNameError(null);

    const body = buildTemplateBody(name);

    setSaving(true);
    try {
      const saved = templateId
        ? await updatePrintableTemplate(templateId, body)
        : await createPrintableTemplate(body);
      applyTemplate(saved);
      await reloadTemplates();
      addToast('Template saved', { variant: 'success' });
    } catch (err) {
      const { formError, fieldErrors } = parseApiError(err);
      if (fieldErrors.name) setNameError(fieldErrors.name);
      addToast(formError || fieldErrors.name || 'Could not save template', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // "Save As" always creates a brand-new document (never overwrites
  // templateId), seeded from the current live design — including any
  // unsaved edits — under a new name entered in the modal below.
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [saveAsError, setSaveAsError] = useState(null);

  const handleSaveAs = () => {
    setSaveAsName(name.trim() ? `${name.trim()} copy` : '');
    setSaveAsError(null);
    setSaveAsOpen(true);
  };

  const confirmSaveAs = async () => {
    if (!saveAsName.trim()) {
      setSaveAsError('Name is required');
      return;
    }
    const body = buildTemplateBody(saveAsName);
    setSaving(true);
    try {
      const saved = await createPrintableTemplate(body);
      applyTemplate(saved);
      await reloadTemplates();
      setSaveAsOpen(false);
      addToast('Template saved as a copy', { variant: 'success' });
    } catch (err) {
      const { formError, fieldErrors } = parseApiError(err);
      setSaveAsError(fieldErrors.name || formError || 'Could not save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!templateId) return;
    const ok = await confirm({
      title: 'Delete template',
      message: `Delete "${name}"? This can't be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    setSaving(true);
    try {
      await deletePrintableTemplate(templateId);
      await reloadTemplates();
      resetToNew();
      addToast('Template deleted', { variant: 'success' });
    } catch (err) {
      addToast('Could not delete template', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const availableWidthIn = Math.max(0, PAGE_WIDTH_IN - pageMargins.left - pageMargins.right);
  const availableHeightIn = Math.max(0, PAGE_HEIGHT_IN - pageMargins.top - pageMargins.bottom);
  const cellWidthIn = availableWidthIn / columns;
  const cellHeightIn = availableHeightIn / rows;

  // Cell padding as a % of that cell's own box (not the page's), so the
  // usable-area rect is correct regardless of the page's on-screen pixel size.
  const cellPaddingPct = (valueIn, cellSizeIn) => (cellSizeIn > 0 ? Math.min(50, (valueIn / cellSizeIn) * 100) : 0);

  // Shared by the grid itself and the margin-visualizer overlay below it —
  // both sit at exactly the page-margin inset, just rendered differently.
  const marginBoxStyle = {
    top: `${(pageMargins.top / PAGE_HEIGHT_IN) * 100}%`,
    left: `${(pageMargins.left / PAGE_WIDTH_IN) * 100}%`,
    right: `${(pageMargins.right / PAGE_WIDTH_IN) * 100}%`,
    bottom: `${(pageMargins.bottom / PAGE_HEIGHT_IN) * 100}%`,
  };

  const gridStyle = {
    ...marginBoxStyle,
    gridTemplateRows: `repeat(${rows}, 1fr)`,
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
  };

  const usableAreaStyle = {
    top: `${cellPaddingPct(cellPadding.top, cellHeightIn)}%`,
    right: `${cellPaddingPct(cellPadding.right, cellWidthIn)}%`,
    bottom: `${cellPaddingPct(cellPadding.bottom, cellHeightIn)}%`,
    left: `${cellPaddingPct(cellPadding.left, cellWidthIn)}%`,
  };

  // Usable area in inches (cell minus its own padding) — the space block
  // x/y/width/height are relative to, on every cell alike.
  const usableWidthIn = Math.max(0, cellWidthIn - cellPadding.left - cellPadding.right);
  const usableHeightIn = Math.max(0, cellHeightIn - cellPadding.top - cellPadding.bottom);

  useEffect(() => {
    const session = groupDragCandidate.current;
    if (!session) return undefined;

    const DRAG_THRESHOLD_PX = 3;

    // Rigid-group clamp: the tightest per-axis bound across every selected
    // block, so the whole group moves as one and stops the instant any
    // member would hit the cell's edge — not each block independently,
    // which would tear the group apart at the boundary.
    const applyGroupDelta = (rawDxIn, rawDyIn) => {
      let lowerDx = -Infinity;
      let upperDx = Infinity;
      let lowerDy = -Infinity;
      let upperDy = Infinity;
      session.startBlocks.forEach(({ x, y, width, height }) => {
        const maxX = Math.max(0, usableWidthIn - (width || 0));
        const maxY = Math.max(0, usableHeightIn - (height || 0));
        lowerDx = Math.max(lowerDx, -x);
        upperDx = Math.min(upperDx, maxX - x);
        lowerDy = Math.max(lowerDy, -y);
        upperDy = Math.min(upperDy, maxY - y);
      });
      const dxIn = Math.min(Math.max(rawDxIn, lowerDx), upperDx);
      const dyIn = Math.min(Math.max(rawDyIn, lowerDy), upperDy);

      BLOCK_TYPES.forEach(({ type }) => {
        const updates = session.startBlocks.filter((sb) => sb.type === type);
        if (updates.length === 0) return;
        settersByType[type]((prev) =>
          prev.map((b) => {
            const start = updates.find((u) => u.id === b.id);
            return start ? { ...b, x: start.x + dxIn, y: start.y + dyIn } : b;
          })
        );
      });
    };

    const handleMove = (e) => {
      const dxPx = e.clientX - session.startMouseX;
      const dyPx = e.clientY - session.startMouseY;
      if (!session.moved) {
        if (Math.hypot(dxPx, dyPx) < DRAG_THRESHOLD_PX) return;
        session.moved = true;
      }
      const rect = cellContentRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;
      applyGroupDelta((dxPx / rect.width) * usableWidthIn, (dyPx / rect.height) * usableHeightIn);
    };

    const handleUp = () => {
      // Detach immediately on mouseup — this effect is keyed on
      // `groupDragTick`, which only changes on the *next* mousedown, so
      // without this the listeners would otherwise linger until then and a
      // stale `handleMove` would keep firing on mouse movement alone (no
      // button held), reading it as past the drag threshold and moving a
      // block the user never grabbed.
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);

      // Never crossed the move threshold — a plain click on a block that
      // was part of a multi-selection collapses the selection down to
      // just this one, matching standard click semantics (only a modifier
      // key or an actual drag preserves the rest of the selection).
      if (!session.moved) {
        setSelectedBlocks([{ type: session.type, id: session.id }]);
      }
      groupDragCandidate.current = null;
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupDragTick, usableWidthIn, usableHeightIn]);

  useEffect(() => {
    const session = marqueeCandidate.current;
    if (!session) return undefined;

    const DRAG_THRESHOLD_PX = 3;

    // Converts the drag's current mouse position (plus the fixed start
    // position) into a rectangle in inches, relative to the usable area —
    // the same coordinate space every block's own x/y/width/height lives
    // in, so it can be hit-tested against them directly.
    const rectFromEvent = (e) => {
      const rect = cellContentRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return null;
      const pxToInX = usableWidthIn / rect.width;
      const pxToInY = usableHeightIn / rect.height;
      const x1 = (session.startMouseX - rect.left) * pxToInX;
      const y1 = (session.startMouseY - rect.top) * pxToInY;
      const x2 = (e.clientX - rect.left) * pxToInX;
      const y2 = (e.clientY - rect.top) * pxToInY;
      return {
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
      };
    };

    const handleMove = (e) => {
      const dxPx = e.clientX - session.startMouseX;
      const dyPx = e.clientY - session.startMouseY;
      if (!session.moved) {
        if (Math.hypot(dxPx, dyPx) < DRAG_THRESHOLD_PX) return;
        session.moved = true;
      }
      const rectIn = rectFromEvent(e);
      if (rectIn) setMarqueeRect(rectIn);
    };

    const handleUp = (e) => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);

      if (session.moved) {
        const rectIn = rectFromEvent(e);
        const matches = [];
        if (rectIn) {
          // Overlap, not full containment — touching any part of the
          // rectangle is enough, the same forgiving hit-test most apps use
          // for rubber-band select.
          BLOCK_TYPES.forEach(({ type }) => {
            blocksByType[type].forEach((b) => {
              const bw = b.width ?? b.size ?? 0;
              const bh = b.height ?? b.size ?? 0;
              const overlaps =
                rectIn.x < b.x + bw && rectIn.x + rectIn.width > b.x && rectIn.y < b.y + bh && rectIn.y + rectIn.height > b.y;
              if (overlaps) matches.push({ type, id: b.id });
            });
          });
        }
        setSelectedBlocks(matches);
      } else {
        // Never crossed the move threshold — a plain click on empty
        // canvas clears the selection.
        setSelectedBlocks([]);
      }
      setMarqueeRect(null);
      marqueeCandidate.current = null;
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marqueeTick, usableWidthIn, usableHeightIn]);

  // PositionPanel/SizePanel/the properties panel only make sense for a
  // single block — they render null (via selectedBlockData) when zero or
  // multiple blocks are selected.
  const singleSelected = selectedBlocks.length === 1 ? selectedBlocks[0] : null;
  const selectedBlockType = singleSelected ? BLOCK_TYPES.find((bt) => bt.type === singleSelected.type) : null;
  const selectedBlockRaw = singleSelected ? blocksByType[singleSelected.type]?.find((b) => b.id === singleSelected.id) : null;
  const selectedBlockData = selectedBlockRaw ? { type: singleSelected.type, ...selectedBlockRaw } : null;

  const isDirty = buildSnapshot({ name, rows, columns, pageMargins, cellPadding, showBoundaries, blocksByType }) !== savedSnapshot;

  // Guard in-app navigation (nav links, the "Back to admin" link, footer/
  // header links, etc.) while there are unsaved changes. Capture-phase so
  // it runs before react-router's own Link click handler navigates.
  // Actual browser navigation (refresh, typed URL, closing the tab) can't
  // be intercepted with app UI — the beforeunload effect below covers that
  // with the browser's own native prompt instead.
  useEffect(() => {
    if (!isDirty) return undefined;

    const handleClick = (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.defaultPrevented) return;
      const anchor = e.target.closest('a');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (!href || /^([a-z]+:)?\/\//i.test(href) || href.startsWith('#')) return;
      if (href === window.location.pathname + window.location.search) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      confirm({
        title: 'Unsaved changes',
        message: 'You have unsaved changes to this template. Leave without saving?',
        confirmLabel: 'Leave without saving',
        cancelLabel: 'Stay',
        danger: true,
      }).then((ok) => {
        if (ok) navigate(href);
      });
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isDirty, navigate, confirm]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return (
    <div className="ss-print">
      <div className="ss-print__canvas">
        <Card className="ss-print__page">
          {showBoundaries && <div className="ss-print__margin-visualizer" style={marginBoxStyle} />}
          <div className="ss-print__grid" style={gridStyle}>
            {Array.from({ length: rows * columns }).map((_, index) => {
              const isMaster = index === 0;
              return (
                <div key={index} className={`ss-print__cell${showBoundaries ? ' ss-print__cell--boundary' : ''}`}>
                  {showBoundaries && <div className="ss-print__cell-usable" style={usableAreaStyle} />}
                  <div
                    className="ss-print__cell-content"
                    style={usableAreaStyle}
                    ref={isMaster ? cellContentRef : undefined}
                    onMouseDown={isMaster ? startMarquee : undefined}
                  >
                    {BLOCK_TYPES.map(({ type, Layer }) => (
                      <Layer
                        key={type}
                        blocks={blocksByType[type]}
                        setBlocks={isMaster ? settersByType[type] : undefined}
                        editable={isMaster}
                        selectedIds={
                          isMaster ? selectedBlocks.filter((sb) => sb.type === type).map((sb) => sb.id) : []
                        }
                        selectionCount={isMaster ? selectedBlocks.length : 0}
                        onSelect={(id, e) => handleSelectBlock(type, id, e)}
                        onGroupDragCandidate={(id, e) => startGroupDragCandidate(type, id, e)}
                        showBoundaries={showBoundaries}
                        usableWidthIn={usableWidthIn}
                        usableHeightIn={usableHeightIn}
                      />
                    ))}
                    {isMaster && marqueeRect && (
                      <div
                        className="ss-print__marquee"
                        style={{
                          left: `${usableWidthIn > 0 ? (marqueeRect.x / usableWidthIn) * 100 : 0}%`,
                          top: `${usableHeightIn > 0 ? (marqueeRect.y / usableHeightIn) * 100 : 0}%`,
                          width: `${usableWidthIn > 0 ? (marqueeRect.width / usableWidthIn) * 100 : 0}%`,
                          height: `${usableHeightIn > 0 ? (marqueeRect.height / usableHeightIn) * 100 : 0}%`,
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
      <div className="ss-print__sidebar">
        <AddBlockMenu
          onAddBlock={addBlock}
          onClearAll={handleClearAllBlocks}
          hasBlocks={BLOCK_TYPES.some(({ type }) => blocksByType[type].length > 0)}
        />
        {selectedBlocks.length > 1 ? (
          <MultiSelectPanel count={selectedBlocks.length} onDelete={deleteSelectedBlocks} />
        ) : (
          selectedBlockType?.PropertiesPanel && (
            <selectedBlockType.PropertiesPanel
              block={selectedBlockRaw}
              onUpdateProperty={(property, value) => patchSelectedBlock({ [property]: value })}
              onDelete={deleteSelectedBlocks}
            />
          )
        )}
        <GridLayoutPanel
          rows={rows}
          columns={columns}
          onRowsChange={setRows}
          onColumnsChange={setColumns}
          showBoundaries={showBoundaries}
          onShowBoundariesChange={setShowBoundaries}
        />
        <PositionPanel
          selectedBlock={selectedBlockData}
          onUpdatePosition={(axis, value) => patchSelectedBlock({ [axis]: value })}
        />
        <SizePanel selectedBlock={selectedBlockData} cellWidthIn={cellWidthIn} cellHeightIn={cellHeightIn} />
        <TemplatePanel
          templates={templates}
          templatesLoading={templatesLoading}
          selectedId={templateId}
          onSelect={handleSelectTemplate}
          name={name}
          onNameChange={handleNameChange}
          nameError={nameError}
          onSave={handleSave}
          onSaveAs={handleSaveAs}
          onDiscard={handleDiscard}
          onNew={resetToNew}
          onDelete={handleDelete}
          saving={saving}
          isDirty={isDirty}
        />
        <PageMarginsPanel pageMargins={pageMargins} onMarginsChange={setPageMargins} />
        <CellSettingsPanel cellPadding={cellPadding} onCellPaddingChange={setCellPadding} />
      </div>
      {confirmDialog}
      <Modal isOpen={saveAsOpen} onClose={() => setSaveAsOpen(false)} title="Save As">
        <Input
          label="Name"
          value={saveAsName}
          onChange={(e) => setSaveAsName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') confirmSaveAs();
          }}
          placeholder="e.g. 2x3 shelf labels"
          disabled={saving}
          error={saveAsError}
          autoFocus
        />
        <div className="ss-form__actions">
          <Button size="sm" onClick={confirmSaveAs} disabled={saving || !saveAsName.trim()}>
            {saving ? <Spinner label="Saving" /> : 'Save'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSaveAsOpen(false)} disabled={saving}>
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
}
